var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { Suspense, useContext } from 'react';
import NextLink from 'next/link';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { ThemeContext } from '../../context/ThemeContext';
import * as Styled from './RichText.styled';
import * as Shared from '../../ContentSystem.shared.styles';
import { ContainerQueryContext } from '../../../../util/ContainerQuery.context';
var Layer2CTA = React.lazy(function () { return import('../Layer2CTA/Layer2CTA'); });
/**
 * Ensure that a block is rendered only if it has children
 *
 * @param Component React component
 * @param props React component props
 * @param tag HTML tag to render as
 * @returns React component
 */
var renderBlock = function (Component, props, tag) {
    return function (node, children) {
        var nonEmptyChildren = React.Children.toArray(children).filter(function (child) {
            // Exclude falsey values (e.g., null, undefined, false, 0) and empty strings
            return child && child !== '';
        });
        return nonEmptyChildren.length ? (tag ? (React.createElement(Component, __assign({ as: tag }, props), children)) : (React.createElement(Component, __assign({}, props), children))) : null;
    };
};
var renderText = function (text) {
    return text
        .split('\n')
        .reduce(function (children, textSegment, index) { return __spreadArray(__spreadArray([], children, true), [
        index > 0 ? React.createElement("br", { key: index }) : null,
        textSegment
    ], false); }, []);
};
var RichText = function (_a) {
    var _b, _c;
    var richText = _a.richText, _d = _a.config, config = _d === void 0 ? undefined : _d, _e = _a.headerStyle, headerStyle = _e === void 0 ? 'SANS_SERIF' : _e, lightHexCode = _a.lightHexCode, headerMaxLineLength = _a.headerMaxLineLength, bulletType = _a.bulletType, listStyle = _a.listStyle;
    var typography = useContext(ThemeContext).typography;
    var styleQueryMode = useContext(ContainerQueryContext).styleQueryMode;
    /**
     * Specify how certain types should be rendered
     */
    var options = {
        renderMark: (_b = {},
            _b[MARKS.BOLD] = function (text) { return React.createElement(Styled.Bold, null, text); },
            _b[MARKS.ITALIC] = function (text) { return (React.createElement(Styled.Italic, null, text)); },
            _b),
        renderNode: (_c = {},
            _c[BLOCKS.PARAGRAPH] = renderBlock(Shared.Body, {
                $size: config === null || config === void 0 ? void 0 : config.body,
                lightHexCode: lightHexCode,
                headerStyle: headerStyle,
                styleSettings: typography === null || typography === void 0 ? void 0 : typography.body,
                $styleQueryMode: styleQueryMode
            }, 'p'),
            _c[BLOCKS.HEADING_1] = renderBlock(Shared.Header, {
                $size: config === null || config === void 0 ? void 0 : config.header,
                headerStyle: headerStyle,
                lightHexCode: lightHexCode,
                headerMaxLineLength: headerMaxLineLength,
                styleSettings: typography === null || typography === void 0 ? void 0 : typography.h1,
                $styleQueryMode: styleQueryMode
            }, 'h1'),
            _c[BLOCKS.HEADING_2] = renderBlock(Shared.Header, {
                $size: config === null || config === void 0 ? void 0 : config.header,
                headerStyle: headerStyle,
                lightHexCode: lightHexCode,
                headerMaxLineLength: headerMaxLineLength,
                styleSettings: typography === null || typography === void 0 ? void 0 : typography.h2,
                $styleQueryMode: styleQueryMode
            }, 'h2'),
            _c[BLOCKS.HEADING_3] = renderBlock(Shared.Header, {
                $size: (config === null || config === void 0 ? void 0 : config.header_3) || (config === null || config === void 0 ? void 0 : config.header),
                headerStyle: headerStyle,
                lightHexCode: lightHexCode,
                headerMaxLineLength: headerMaxLineLength,
                styleSettings: typography === null || typography === void 0 ? void 0 : typography.h3,
                $styleQueryMode: styleQueryMode
            }, 'h3'),
            _c[BLOCKS.HEADING_4] = renderBlock(Shared.Header, {
                $size: config === null || config === void 0 ? void 0 : config.header,
                headerStyle: headerStyle,
                lightHexCode: lightHexCode,
                headerMaxLineLength: headerMaxLineLength,
                styleSettings: typography === null || typography === void 0 ? void 0 : typography.h4,
                $styleQueryMode: styleQueryMode
            }, 'h4'),
            _c[INLINES.HYPERLINK] = function (node, children) { return (React.createElement(NextLink, { href: node.data.uri },
                React.createElement(Shared.CTA, { "$size": config === null || config === void 0 ? void 0 : config.cta, href: node.data.uri, ctaStyle: "LINK", lightHexCode: lightHexCode, "$styleQueryMode": styleQueryMode }, children))); },
            _c[BLOCKS.UL_LIST] = function (node, children) { return (React.createElement(Styled.List, { "$bulletType": bulletType, "$listStyle": listStyle, "$lightHexCode": lightHexCode, styleSettings: typography === null || typography === void 0 ? void 0 : typography.body, "$styleQueryMode": styleQueryMode }, children)); },
            _c[INLINES.EMBEDDED_ENTRY] = function (node) {
                switch (node.data.target.contentType) {
                    case 'layer2CTA':
                        return (React.createElement(Suspense, { fallback: null },
                            React.createElement(Layer2CTA, { size: config === null || config === void 0 ? void 0 : config.cta, cta: node.data.target.text || node.data.target.label, title: node.data.target.resource.title, sections: node.data.target.resource.sections, defaultSection: node.data.target.resource.defaultSection })));
                    default:
                }
            },
            _c),
        renderText: renderText
    };
    return (React.createElement(React.Fragment, null, documentToReactComponents(richText, options)));
};
export default RichText;
